import { Head } from "vite-react-ssg";
import { Section, SectionHeading, Stat, Pill } from "@/components/kit";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Eyebrow } from "@/components/Eyebrow";
import { HeroBackdrop } from "@/components/HeroBackdrop";
import enterpriseHero from "@/assets/hero/enterprise-hero.jpg";
import {
  ENTERPRISE_HERO,
  CAPABILITY_CARDS,
  OPEN_PORTABLE,
  TRUST_PILLS,
  ENTERPRISE_STATS,
  ENTERPRISE_CTA,
} from "@/content/enterprise";

export default function Enterprise() {
  return (
    <>
      <Head>
        <title>Enterprise — DjiniousLab</title>
        <meta
          name="description"
          content="DjiniousLab is built for engineering teams — OIDC SSO, multi-tenant isolation, OTLP observability, audit logging, and FMI 3.0 interop. Production-grade from day one."
        />
      </Head>

      {/* 1. Hero */}
      <HeroBackdrop image={enterpriseHero}>
        <Eyebrow>{ENTERPRISE_HERO.eyebrow}</Eyebrow>
        <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          {ENTERPRISE_HERO.title}
        </h1>
        <p className="mt-5 text-xl leading-relaxed text-muted-foreground">
          {ENTERPRISE_HERO.intro}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button to="/book-demo">Book a demo</Button>
          <Button to="/features" variant="ghost">
            Explore features
          </Button>
        </div>
      </HeroBackdrop>

      {/* 2. Stat strip */}
      <Section bordered>
        <div className="grid grid-cols-2 gap-y-10 sm:grid-cols-4">
          {ENTERPRISE_STATS.map((s) => (
            <Stat key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </Section>

      {/* 3. Capability grid */}
      <Section wash="wash">
        <SectionHeading
          eyebrow="CAPABILITIES"
          title="Security and operations built into the platform."
          intro="Every capability below ships in the standard deployment — there is no enterprise add-on tier, no custom integration project, and no per-feature licensing."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITY_CARDS.map((card) => (
            <Card key={card.title} className="flex flex-col gap-3">
              <h3 className="text-sm font-bold text-foreground">{card.title}</h3>
              <p className="flex-1 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
            </Card>
          ))}
        </div>
      </Section>

      {/* 4. Open & portable */}
      <Section wash="surface">
        <div className="mx-auto max-w-3xl text-center">
          <Eyebrow>{OPEN_PORTABLE.eyebrow}</Eyebrow>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">
            {OPEN_PORTABLE.title}
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
            {OPEN_PORTABLE.intro}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-center shadow-soft">
              <div className="text-sm font-bold text-foreground">FMI 3.0</div>
              <div className="mt-0.5 text-xs text-muted-foreground">import / export</div>
            </div>
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-center shadow-soft">
              <div className="text-sm font-bold text-foreground">Git-native</div>
              <div className="mt-0.5 text-xs text-muted-foreground">project storage</div>
            </div>
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-center shadow-soft">
              <div className="text-sm font-bold text-foreground">Rust codegen</div>
              <div className="mt-0.5 text-xs text-muted-foreground">firmware output</div>
            </div>
            <div className="rounded-xl border border-border bg-card px-5 py-4 text-center shadow-soft">
              <div className="text-sm font-bold text-foreground">Air-gap ready</div>
              <div className="mt-0.5 text-xs text-muted-foreground">single compose file</div>
            </div>
          </div>
        </div>
      </Section>

      {/* 5. Trust strip */}
      <Section bordered>
        <div className="flex flex-col items-center gap-6 text-center">
          <SectionHeading
            eyebrow="SECURITY POSTURE"
            title="Designed for security-conscious teams."
            intro="DjiniousLab is designed with a SOC 2-aligned threat model. The platform does not claim certifications it has not completed — but every control below is implemented and verifiable."
          />
          <div className="flex flex-wrap justify-center gap-3">
            {TRUST_PILLS.map((p) => (
              <Pill key={p.label} tone={p.tone}>
                {p.label}
              </Pill>
            ))}
          </div>
        </div>
      </Section>

      {/* 6. Deployment note */}
      <Section wash="wash">
        <div className="grid items-start gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <Eyebrow>DEPLOYMENT</Eyebrow>
            <h2 className="mt-4 text-2xl font-extrabold tracking-tight sm:text-3xl">
              Run it your way.
            </h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              The same binary ships to every target. Choose the model that fits your organisation's
              security boundary.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 lg:col-span-2">
            <Card className="flex flex-col gap-2">
              <h3 className="text-sm font-bold text-foreground">Cloud-hosted</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Managed deployment on the region of your choice. Automatic upgrades, monitoring, and
                backup included.
              </p>
            </Card>
            <Card className="flex flex-col gap-2">
              <h3 className="text-sm font-bold text-foreground">Self-hosted</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Deploy to your own Kubernetes cluster or VM fleet. Docker Compose and Helm chart
                provided. Full control over data residency.
              </p>
            </Card>
            <Card className="flex flex-col gap-2">
              <h3 className="text-sm font-bold text-foreground">Air-gapped</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Completely offline. All images, toolchains, and dependencies ship in a single OCI
                bundle — no outbound internet required at runtime.
              </p>
            </Card>
          </div>
        </div>
      </Section>

      {/* 7. CTA block */}
      <section className="bg-block">
        <div className="mx-auto max-w-6xl px-6 py-20 text-center">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {ENTERPRISE_CTA.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg leading-relaxed text-white/80">
            {ENTERPRISE_CTA.body}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button to={ENTERPRISE_CTA.cta.to} variant="onblock">
              {ENTERPRISE_CTA.cta.label}
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
